import React from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

import * as url from "helper/UrlHelper";

import { getCustomStyles } from "Config/Config";
import DateTimeField from "components/Common/DateTimeField/DateTimeField";
import FileUploadField from "components/Common/FileUploadField/FileUploadField";
import JoditEditorField from "components/Common/JoditEditorField/JoditEditorField";
import ModeCard from "components/Common/ModeCard/ModeCard";
import MultiSelectField from "components/Common/MultiSelectField/MultiSelectField";

const EventForm = ({
  setParentEventTitle,
  eventData,
  setEventField,
  eventTypeList,
  categoryList,
  interestList,
  searchingList,
  companyList,
  parentEventOptions,
  setCompanyInput,
  handleLocationFromZip,
  handleImageUpload,
  handleDocumentUpload,
  handleSubmit,
  t,
  isEditMode,
  isSaving,
  followersOptions,
  setFollowersInput,
}) => {
  return (
    <div className="mt-3 p-3 p-lg-4 border border-gray-300 rounded-10 shadow-sm">
      {/* Basic Info Section */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">{t("Basic Info")}</h5>

        {/* Event Type and Company */}
        <div className="row">
          <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
            <label className="d-block fs-sm fw-semibold mb-2">
              {t("Type")}
            </label>
            <Select
              styles={getCustomStyles()}
              options={eventTypeList}
              value={eventData?.eventType}
              onChange={(val) => setEventField("eventType")(val)}
            />
          </div>
          <div className="col-lg-6">
            <label className="d-block fs-sm fw-semibold mb-2">
              {t("Company")}
            </label>
            <Select
              styles={getCustomStyles()}
              placeholder={t("Select or type company name")}
              isClearable
              options={companyList}
              value={eventData?.company}
              onChange={setEventField("company")}
              onInputChange={setCompanyInput}
            />
          </div>
        </div>
      </div>

      {/* Date and Time Fields */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <div className="row">
          <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
            <DateTimeField
              label={t("Start Date & Time")}
              dateValue={eventData?.startDate}
              timeValue={eventData?.startTime}
              onDateChange={(e) => setEventField("startDate")(e.target.value)}
              onTimeChange={(e) => setEventField("startTime")(e.target.value)}
            />
          </div>
          <div className="col-lg-6">
            <DateTimeField
              label={t("End Date & Time")}
              dateValue={eventData?.endDate}
              timeValue={eventData?.endTime}
              onDateChange={(e) => setEventField("endDate")(e.target.value)}
              onTimeChange={(e) => setEventField("endTime")(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Buy/Sell Participation Fee */}
      {eventData?.eventType?.slug === "buysell" && (
        <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
          <div className="row">
            <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
              <label className="d-block fs-sm fw-semibold mb-2">
                {t("Buy Participation Fee")}
              </label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control fs-sm shadow-none border border-gray-300"
                  value={eventData?.buyFee}
                  onChange={(e) => setEventField("buyFee")(e.target.value)}
                  placeholder={t("Enter buy participation fee")}
                />
                <span className="input-group-text">€</span>
              </div>
            </div>
            <div className="col-lg-6">
              <label className="d-block fs-sm fw-semibold mb-2">
                {t("Sell Participation Fee")}
              </label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control fs-sm shadow-none border border-gray-300"
                  value={eventData?.sellFee}
                  onChange={(e) => setEventField("sellFee")(e.target.value)}
                  placeholder={t("Enter sell participation fee")}
                />
                <span className="input-group-text">€</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <label className="d-block fs-sm fw-semibold mb-2">
          {t("Title")} <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className={`form-control fs-sm shadow-none ${
            eventData?.validation?.title
              ? "is-invalid"
              : "border border-gray-300"
          }`}
          placeholder={t("e.g. Marketing Initiative")}
          value={eventData?.title}
          onChange={(e) => {
            setEventField("title")(e.target.value);
            setEventField("validation")({
              ...(eventData?.validation || {}),
              title: false,
            });
            setEventField("error")("");
          }}
        />
        {eventData?.validation?.title && (
          <div className="invalid-feedback fs-md">
            {t("Title is required")} *
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <label className="d-block fs-sm fw-semibold mb-2">{t("Summary")}</label>
        <textarea
          className="form-control fs-sm shadow-none border border-gray-300"
          placeholder={t("Provide a brief summary of the event")}
          rows={5}
          value={eventData?.summary}
          onChange={(e) => setEventField("summary")(e.target.value)}
        />
      </div>

      {/* Description */}
      <div className="form-group mb-4 p-3 pb-0 border rounded-8 shadow-sm bg-white">
        <JoditEditorField
          label={t("Description")}
          labelClassName="d-block fs-sm fw-semibold mb-2"
          value={eventData?.description}
          onChange={(newContent) => setEventField("description")(newContent)}
        />
      </div>

      {/* --- Is Root and Select Parent --- */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <div className="row">
          <div className="col-lg-2 mb-3 mb-lg-0">
            <div className="form-check d-flex align-items-center gap-2 fs-sm p-0 m-0 mb-4">
              <input
                className="form-check-input bg-transparent border-2 border-gray-600 shadow-none m-0"
                type="checkbox"
                checked={eventData?.isRoot}
                onChange={(e) => {
                  setEventField("isRoot")(e.target.checked);
                  setEventField("validation")({
                    ...(eventData?.validation || {}),
                    parentEvent: false,
                  });
                  setEventField("error")("");
                }}
                id="childEventCheck"
              />
              <label
                className="form-check-label fs-sm fw-semibold mb-0"
                htmlFor="childEventCheck"
              >
                {t("This is a child event")}
              </label>
            </div>
          </div>
          {eventData?.isRoot && (
            <div className="col-lg-10">
              <Select
                isClearable
                styles={getCustomStyles(eventData?.validation?.parentEvent)}
                options={parentEventOptions}
                placeholder={t("Search for parent event by title")}
                value={eventData?.parentEvent}
                onChange={(val) => {
                  setEventField("parentEvent")(val);
                  setEventField("validation")({
                    ...(eventData?.validation || {}),
                    parentEvent: false,
                  });
                  setEventField("error")("");
                }}
                onInputChange={setParentEventTitle}
              />
              {eventData?.validation?.parentEvent && (
                <div className="invalid-feedback d-block">
                  <p className="d-flex align-items-center gap-1 text-danger">
                    <span className="material-symbols-outlined">warning</span>
                    <span>{t("Please select parent event")} !</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* --- End Is Root and Select Parent --- */}

      {/* Mode of Event Section */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">{t("Mode of Event")}</h5>
        <div className="row">
          <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
            <ModeCard
              title={t("Physical")}
              checked={eventData?.isPhysical}
              onChange={(val) => setEventField("isPhysical")(val)}
            >
              <div>
                <div className="mb-2 d-flex align-items-center">
                  <label
                    className="me-2 mb-0 fs-sm fw-semibold"
                    style={{ minWidth: "60px" }}
                  >
                    Address
                  </label>
                  <input
                    type="text"
                    className="form-control fs-sm shadow-none border border-gray-300"
                    placeholder={t("Enter event address")}
                    value={eventData?.location}
                    onChange={(e) => setEventField("location")(e.target.value)}
                  />
                </div>

                <div className="mb-2 d-flex align-items-center">
                  <label
                    className="me-2 mb-0 fs-sm fw-semibold"
                    style={{ minWidth: "60px" }}
                  >
                    City
                  </label>
                  <input
                    type="text"
                    className="form-control fs-sm shadow-none border border-gray-300"
                    placeholder={t("Enter city")}
                    value={eventData?.city}
                    onChange={(e) => setEventField("city")(e.target.value)}
                  />
                </div>

                <div className="mb-2 d-flex align-items-center">
                  <label
                    className="me-2 mb-0 fs-sm fw-semibold"
                    style={{ minWidth: "60px" }}
                  >
                    Country
                  </label>
                  <input
                    type="text"
                    className="form-control fs-sm shadow-none border border-gray-300"
                    placeholder={t("Enter country")}
                    value={eventData?.country}
                    onChange={(e) => setEventField("country")(e.target.value)}
                  />
                </div>

                <div className="mb-2 d-flex align-items-center">
                  <label
                    className="me-2 mb-0 fs-sm fw-semibold"
                    style={{ minWidth: "60px" }}
                  >
                    Zipcode
                  </label>
                  <input
                    type="text"
                    className="form-control fs-sm shadow-none border border-gray-300"
                    placeholder={t("Enter zipcode")}
                    value={eventData?.zipcode}
                    onChange={(e) => setEventField("zipcode")(e.target.value)}
                  />
                </div>

                <div className="mb-2 d-flex align-items-center">
                  <label
                    className="me-2 mb-0 fs-sm fw-semibold"
                    style={{ minWidth: "60px" }}
                  >
                    Map URL
                  </label>
                  <input
                    type="text"
                    className="form-control fs-sm shadow-none border border-gray-300"
                    placeholder={t("Enter map URL")}
                    value={eventData?.mapUrl}
                    onChange={(e) => setEventField("mapUrl")(e.target.value)}
                  />
                </div>
              </div>
            </ModeCard>
          </div>
          <div className="col-lg-6">
            <ModeCard
              title={`${t("Virtual")} / ${t("Online")}`}
              checked={eventData?.isVirtual}
              onChange={(val) => setEventField("isVirtual")(val)}
            >
              <input
                type="text"
                className="form-control fs-sm shadow-none border border-gray-300"
                placeholder={t("Enter virtual meeting URL")}
                value={eventData?.virtualUrl}
                onChange={(e) => setEventField("virtualUrl")(e.target.value)}
              />
            </ModeCard>
          </div>
        </div>
      </div>

      {/* Optional Information Section */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">{t("Optional Information")}</h5>
        <div className="row">
          <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
            <input
              type="text"
              className="form-control fs-sm shadow-none border border-gray-300"
              placeholder={t("Enter contact phone number")}
              value={eventData?.contactPhone}
              onChange={(e) => setEventField("contactPhone")(e.target.value)}
            />
          </div>
          <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
            <input
              type="email"
              className="form-control fs-sm shadow-none border border-gray-300"
              placeholder={t("Enter contact email address")}
              value={eventData?.contactEmail}
              onChange={(e) => setEventField("contactEmail")(e.target.value)}
            />
          </div>
          <div className="col-lg-4">
            <input
              type="text"
              className="form-control fs-sm shadow-none border border-gray-300"
              placeholder={t("Enter website URL")}
              value={eventData?.website}
              onChange={(e) => setEventField("website")(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4">
          <MultiSelectField
            label={t("Category")}
            options={categoryList}
            value={eventData?.category}
            onChange={setEventField("category")}
            placeholder={t("Select event categories")}
            styles={getCustomStyles()}
          />
        </div>

        <div className="mt-4">
          <MultiSelectField
            label={t("Searching")}
            options={searchingList}
            value={eventData?.searching}
            onChange={setEventField("searching")}
            placeholder={t("Select searching criteria")}
            styles={getCustomStyles()}
          />
        </div>

        <div className="mt-4">
          <MultiSelectField
            label={t("Interest")}
            options={interestList}
            value={eventData?.interest}
            onChange={setEventField("interest")}
            placeholder={t("Select areas of interest")}
            styles={getCustomStyles()}
          />
        </div>

        <div className="mt-4">
          <MultiSelectField
            label={t("Followers")}
            options={followersOptions}
            value={eventData?.followers}
            onChange={setEventField("followers")}
            placeholder={t("Type user name or email")}
            styles={getCustomStyles()}
            onInputChange={setFollowersInput}
          />
        </div>
      </div>

      {/* Banner and Documents Section */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-4">{t("Banner and Documents")}</h5>

        <div className="mb-4">
          <label className="d-block fs-sm fw-semibold mb-2">
            {t("Event Banner")}
          </label>
          <FileUploadField
            label={t("Banner")}
            accept="jpg png jpeg"
            onUpload={handleImageUpload}
            files={
              eventData?.image === ""
                ? [
                    {
                      path: "https://placehold.co/150x150",
                      name: "Event Banner",
                    },
                  ]
                : [
                    {
                      path: url.SERVER_URL + eventData?.image,
                      name: "Event Banner",
                    },
                  ]
            }
            onRemove={() => {
              setEventField("image")("");
              setEventField("imageId")(null);
            }}
            loading={eventData?.isUploadingImage}
            fileType="image"
          />
        </div>

        <div className="mb-0">
          <label className="d-block fs-sm fw-semibold mb-2">
            {t("Documents")}
          </label>
          <FileUploadField
            label={t("Documents")}
            accept="doc mp4 pdf"
            onUpload={handleDocumentUpload}
            files={eventData?.documents}
            onRemove={(index) => {
              const updatedDocs = [...(eventData?.documents || [])];
              const updatedDocIds = [...(eventData?.documentIds || [])];
              updatedDocs.splice(index, 1);
              updatedDocIds.splice(index, 1);
              setEventField("documents")(updatedDocs);
              setEventField("documentIds")(updatedDocIds);
            }}
            loading={eventData?.isUploadingDocs}
            multiple
            fileType="document"
          />
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">{t("Terms and Conditions")}</h5>
        <div className="form-check mb-2">
          <input
            className="form-check-input me-2"
            type="checkbox"
            checked={eventData?.isPrivate}
            onChange={(e) => setEventField("isPrivate")(e.target.checked)}
            id="privateEvent"
          />
          <label className="form-check-label" htmlFor="privateEvent">
            {t("Mark as private event")}
          </label>
        </div>

        {!eventData?.isPrivate && (
          <div className="form-check mb-2">
            <input
              className="form-check-input me-2"
              type="checkbox"
              checked={eventData?.autoAccept}
              onChange={(e) => setEventField("autoAccept")(e.target.checked)}
              id="autoAccept"
            />
            <label className="form-check-label" htmlFor="autoAccept">
              {t("Auto accept joining invitations")}
            </label>
          </div>
        )}
      </div>

      {/* Error Message */}
      {eventData?.error && eventData?.error !== "" && (
        <div className="alert alert-danger" role="alert">
          {eventData?.error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="action d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <Link
          to="/admin/events/list"
          className="btn btn-outline-primary d-flex align-items-center gap-2"
        >
          <span className="material-symbols-outlined">cancel</span>
          {t("Cancel")}
        </Link>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
          disabled={isSaving}
          onClick={handleSubmit}
        >
          {isSaving ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {t("Saving...")}
            </>
          ) : (
            <>
              <span className="material-symbols-outlined">save</span>
              {isEditMode ? t("Update Event") : t("Save Event")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default EventForm;
